import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Check, Eye, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { InvitesPanel } from "@/components/crm/InvitesPanel";
import { MembersPanel } from "@/components/crm/MembersPanel";
import { PermissionsPanel } from "@/components/crm/PermissionsPanel";
import { TeamSalesPanel } from "@/components/crm/TeamSalesPanel";
import { useOrg } from "@/lib/crm/useOrg";
import {
  listTeamData,
  nominateReport,
  removeReportLink,
  respondManagerLink,
  teamTimeline,
  teamWorkload,
} from "@/lib/team.functions";
import { TimelineChart } from "@/components/timeline/TimelineChart";
import { GROUPINGS, type TimelineGrouping } from "@/lib/timeline";

export const Route = createFileRoute("/_authenticated/team")({
  head: () => ({
    meta: [
      { title: "Team — MyPriorities" },
      {
        name: "description",
        content:
          "Manager view of your direct reports: open tasks, hours landing this week, overdue work and recent completions.",
      },
      { property: "og:title", content: "Team — MyPriorities" },
      {
        property: "og:description",
        content: "Workload at a glance for the people who report to you.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: TeamPage,
});

function TeamPage() {
  const queryClient = useQueryClient();
  const fetchTeam = useServerFn(listTeamData);
  const fetchWorkload = useServerFn(teamWorkload);
  const nominate = useServerFn(nominateReport);
  const respond = useServerFn(respondManagerLink);
  const remove = useServerFn(removeReportLink);
  const [code, setCode] = useState("");
  const fetchTimeline = useServerFn(teamTimeline);
  const [grouping, setGrouping] = useState<TimelineGrouping>("owner");

  const team = useQuery({ queryKey: ["team-data"], queryFn: () => fetchTeam() });
  const workload = useQuery({ queryKey: ["team-workload"], queryFn: () => fetchWorkload() });
  const timeline = useQuery({ queryKey: ["team-timeline"], queryFn: () => fetchTimeline() });

  const org = useOrg();

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["team-data"] });
    queryClient.invalidateQueries({ queryKey: ["team-workload"] });
  };

  const add = useMutation({
    mutationFn: () => nominate({ data: { code } }),
    onSuccess: (result) => {
      if (result && "error" in result) {
        toast.error(result.error);
        return;
      }
      toast.success("Invite sent — they need to confirm before you see their workload.");
      setCode("");
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const answer = useMutation({
    mutationFn: (input: { id: string; confirm: boolean }) => respond({ data: input }),
    onSuccess: (result, input) => {
      if (result && "error" in result) {
        toast.error(result.error);
        return;
      }
      toast.success(input.confirm ? "Manager access confirmed" : "Access revoked");
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const drop = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Report removed");
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const reports = team.data?.reports ?? [];
  const managers = team.data?.managers ?? [];
  const loads = workload.data ?? [];

  return (
    <AppShell
      title="Team hub"
      subtitle="Invite people, see who reports to whom, and how the team is performing."
    >
      <CollapsibleSection
        storageKey="team-members"
        title="People and access levels"
        description="Create ready-to-use account IDs, and change who's an admin, manager or standard user."
        summary={org.data?.organisation ? org.data.organisation.name : "No organisation yet"}
      >
        <MembersPanel orgId={org.data?.organisation?.id ?? null} role={org.data?.role ?? null} />
      </CollapsibleSection>

      <CollapsibleSection
        storageKey="team-invites"
        title="Invite codes"
        description="Share a code so someone can join your organisation — no email needed."
        summary={org.data?.organisation ? org.data.organisation.name : "No organisation yet"}
      >
        <InvitesPanel orgId={org.data?.organisation?.id ?? null} role={org.data?.role ?? null} />
      </CollapsibleSection>

      <CollapsibleSection
        storageKey="team-permissions"
        title="Permissions"
        description="Fine-tune what each access level can do — exports, visuals, slides, revenue and setup."
        summary={org.data?.role === "admin" ? "Editable" : "View only"}
      >
        <PermissionsPanel
          orgId={org.data?.organisation?.id ?? null}
          role={org.data?.role ?? null}
        />
      </CollapsibleSection>

      {org.data?.organisation ? (
        <CollapsibleSection
          storageKey="team-sales"
          title="Sales performance by owner"
          description="Pipeline, weighted forecast, closed won and contracted revenue per person."
          summary="Export, chart or turn into slides"
        >
          <TeamSalesPanel orgId={org.data.organisation.id} />
        </CollapsibleSection>
      ) : null}

      <CollapsibleSection
        storageKey="team-reports"
        title="Direct reports"
        description="Nominate someone by access code. They confirm before any workload is shared."
        summary={`${reports.filter((r) => r.status === "confirmed").length} confirmed · ${reports.filter((r) => r.status === "pending").length} pending`}
      >
        <div className="panel mt-3 space-y-4 p-5">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-2">
              <Label htmlFor="report-code">Their access code</Label>
              <Input
                id="report-code"
                placeholder="SGL-XXXXXX"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-48"
              />
            </div>
            <Button onClick={() => add.mutate()} disabled={add.isPending || !code.trim()}>
              <UserPlus className="size-4" /> Nominate
            </Button>
          </div>

          {reports.length === 0 ? (
            <p className="text-sm text-muted-foreground">No reports yet.</p>
          ) : (
            <div className="divide-y divide-border">
              {reports.map((link) => {
                const load = loads.find((l) => l.link_id === link.id);
                return (
                  <div key={link.id} className="flex flex-wrap items-center gap-3 py-3">
                    <span className="font-medium">
                      {link.counterpart_name ?? link.counterpart_code ?? "Unknown"}
                    </span>
                    <Badge variant={link.status === "confirmed" ? "default" : "outline"}>
                      {link.status === "confirmed"
                        ? "Confirmed"
                        : link.status === "pending"
                          ? "Awaiting their confirmation"
                          : "Revoked"}
                    </Badge>
                    {load ? (
                      <span className="text-xs text-muted-foreground">
                        {load.open_tasks} open · {load.week_hours}h this week · {load.overdue}{" "}
                        overdue · {load.recent_completions} done in 7 days
                        {load.top_clients.length
                          ? ` · ${load.top_clients.map((c) => `${c.name} (${c.count})`).join(", ")}`
                          : ""}
                      </span>
                    ) : null}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="ml-auto"
                      aria-label="Remove report"
                      onClick={() => drop.mutate(link.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        storageKey="team-managers"
        title="Who can see your workload"
        description="Confirm a manager to share your open tasks, hours and completions summary."
        summary={`${managers.filter((m) => m.status === "confirmed").length} confirmed`}
      >
        <div className="panel mt-3 p-5">
          {managers.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nobody has nominated you.</p>
          ) : (
            <div className="divide-y divide-border">
              {managers.map((link) => (
                <div key={link.id} className="flex flex-wrap items-center gap-3 py-3">
                  <span className="font-medium">
                    {link.counterpart_name ?? link.counterpart_code ?? "Unknown"}
                  </span>
                  <Badge variant={link.status === "confirmed" ? "default" : "outline"}>
                    {link.status === "confirmed"
                      ? "Sharing"
                      : link.status === "pending"
                        ? "Wants access"
                        : "Revoked"}
                  </Badge>
                  <div className="ml-auto flex gap-2">
                    {link.status !== "confirmed" ? (
                      <Button
                        size="sm"
                        onClick={() => answer.mutate({ id: link.id, confirm: true })}
                        disabled={answer.isPending}
                      >
                        <Check className="size-4" /> Confirm
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => answer.mutate({ id: link.id, confirm: false })}
                        disabled={answer.isPending}
                      >
                        <X className="size-4" /> Revoke
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        storageKey="team-timeline"
        title="Timeline"
        description="Your work alongside every confirmed report, with QC and sign-off milestones."
        summary={`${timeline.data?.length ?? 0} items`}
      >
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <p className="text-xs text-muted-foreground">
            Not much on your timeline yet? See a worked example.
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/timeline-example">
              <Eye className="size-4" /> View example timeline
            </Link>
          </Button>
        </div>
        <div className="mt-3">
          <TimelineChart
            tasks={timeline.data ?? []}
            grouping={grouping}
            onGroupingChange={setGrouping}
            names={{ pillars: {}, goals: {} }}
            groupings={GROUPINGS.filter((g) => g.value !== "pillar" && g.value !== "goal")}
            emptyMessage="Once you or your reports add start dates, due dates or internal deadlines, the bars appear here."
          />
        </div>
      </CollapsibleSection>
    </AppShell>
  );
}
